import i18n from 'modules/core/lib/i18n'

import { type ManualApproveHandlerSchema } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/manualApproveHandler'
import { type DurationUnit } from 'modules/workflows/lib/durationUnit'

export type UIType = 'none' | 'team' | 'manual_selected_employees' | 'role'

export type StrategyItem = {
  id: string
  type: string
  employeeId?: number
  teamId?: number
  roleId?: string
}

export const getStrategyOptions = (): { label: string; value: string; ui_type: UIType }[] => [
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.employee_manager'),
    value: 'employee_manager',
    ui_type: 'none' as const,
  },
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.admins'),
    value: 'admins',
    ui_type: 'none' as const,
  },
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.anyone_in_team'),
    value: 'anyone_in_team',
    ui_type: 'team' as const,
  },
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.team_leads'),
    value: 'team_leads',
    ui_type: 'team' as const,
  },
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.manual_selection'),
    value: 'manual_selection',
    ui_type: 'manual_selected_employees' as const,
  },
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.job_catalog_role'),
    value: 'job_catalog_role',
    ui_type: 'role' as const,
  },
]

export const getStrategyType = (strategyValue: string): UIType => {
  return getStrategyOptions().find((s) => s.value === strategyValue)?.ui_type || 'none'
}

export const parseStrategiesFromPayload = (
  payload: ManualApproveHandlerSchema['nodeData']['payload'],
  isFallback = false
): StrategyItem[] => {
  const strategies: StrategyItem[] = []
  const prefix = isFallback ? 'fallback_' : ''

  const reviewerIds = isFallback ? payload.fallback_reviewer_ids : payload.reviewer_ids
  const employeeSegments = isFallback
    ? payload.fallback_employee_segments
    : payload.employee_segments

  if (reviewerIds && Array.isArray(reviewerIds) && reviewerIds.length > 0) {
    reviewerIds.forEach((employeeId) => {
      if (typeof employeeId === 'number') {
        strategies.push({
          id: `${prefix}manual_selection_${employeeId}`,
          type: 'manual_selection',
          employeeId: employeeId,
        })
      }
    })
  }

  if (employeeSegments && Array.isArray(employeeSegments)) {
    employeeSegments.forEach((segment, index) => {
      const strategyType = getStrategyType(segment.name)

      if (strategyType === 'team' && segment.input && 'team_id' in segment.input) {
        const teamId = segment.input.team_id
        if (typeof teamId === 'number' && teamId > 0) {
          strategies.push({
            id: `${prefix}${segment.name}_${teamId}_${index}`,
            type: segment.name,
            teamId: teamId,
          })
        }
      } else if (strategyType === 'role' && segment.input && 'role_id' in segment.input) {
        const roleId = segment.input.role_id
        if (typeof roleId === 'string' && roleId.length > 0) {
          strategies.push({
            id: `${prefix}${segment.name}_${roleId}_${index}`,
            type: segment.name,
            roleId: roleId,
          })
        }
      } else if (strategyType === 'none') {
        strategies.push({
          id: `${prefix}${segment.name}_${index}`,
          type: segment.name,
        })
      }
    })
  }

  return strategies
}

const buildStrategiesPayload = (strategies: StrategyItem[]) => {
  const manualSelectionStrategies = strategies.filter((s) => s.type === 'manual_selection')
  const otherStrategies = strategies.filter((s) => s.type !== 'manual_selection')

  const reviewerIds =
    manualSelectionStrategies.length > 0
      ? manualSelectionStrategies
          .map((s) => s.employeeId)
          .filter((id): id is number => id !== undefined)
      : undefined

  const employeeSegments =
    otherStrategies.length > 0
      ? otherStrategies.map((strategy) => {
          if (
            strategy.teamId &&
            (strategy.type === 'anyone_in_team' || strategy.type === 'team_leads')
          ) {
            const segmentName: 'anyone_in_team' | 'team_leads' =
              strategy.type === 'anyone_in_team' ? 'anyone_in_team' : 'team_leads'
            return {
              name: segmentName,
              input: { team_id: strategy.teamId },
            }
          } else if (strategy.type === 'employee_manager' || strategy.type === 'admins') {
            const segmentName: 'employee_manager' | 'admins' =
              strategy.type === 'employee_manager' ? 'employee_manager' : 'admins'
            return {
              name: segmentName,
              input: {},
            }
          } else if (strategy.type === 'job_catalog_role' && strategy.roleId) {
            return {
              name: 'job_catalog_role' as const,
              input: { role_id: strategy.roleId },
            }
          }
          return null
        })
      : undefined

  const filteredEmployeeSegments = employeeSegments?.filter(
    (segment): segment is NonNullable<typeof segment> => segment !== null
  )

  return { reviewerIds, filteredEmployeeSegments }
}

export const strategiesToPayload = (
  strategies: StrategyItem[],
  due_on_value: number,
  due_on_unit: DurationUnit,
  fallbackStrategies: StrategyItem[] = []
): ManualApproveHandlerSchema['nodeData']['payload'] => {
  const { reviewerIds, filteredEmployeeSegments } = buildStrategiesPayload(strategies)
  const {
    reviewerIds: fallbackReviewerIds,
    filteredEmployeeSegments: fallbackEmployeeSegments,
  } = buildStrategiesPayload(fallbackStrategies)

  return {
    due_on_value,
    due_on_unit,
    ...(reviewerIds && reviewerIds.length > 0 ? { reviewer_ids: reviewerIds } : {}),
    ...(filteredEmployeeSegments && filteredEmployeeSegments.length > 0
      ? { employee_segments: filteredEmployeeSegments }
      : {}),
    ...(fallbackReviewerIds && fallbackReviewerIds.length > 0
      ? { fallback_reviewer_ids: fallbackReviewerIds }
      : {}),
    ...(fallbackEmployeeSegments && fallbackEmployeeSegments.length > 0
      ? { fallback_employee_segments: fallbackEmployeeSegments }
      : {}),
  }
}
